import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Link — inline text link, from the Figma link specs (TailGrids link model).
 *
 *   variant   dark | primary
 *   size      xs | sm | md
 *   leftIcon  ReactNode    icon before the text (auto-sized per `size`)
 *   rightIcon ReactNode    icon after the text  (auto-sized per `size`)
 *
 * First Bite text links never underline — hover is a color shade-shift:
 *   dark     gray-800 → gray-500
 *   primary  basil-600 → basil-700
 *
 * Renders a semantic <a>. A focus-visible ring (not in Figma's Default/Hover
 * states) is added for keyboard accessibility. Pass `asChild` to render a
 * router link (e.g. React Router's <Link>) with these styles.
 */
export const linkVariants = cva(
  'inline-flex items-center font-medium tracking-[-0.2px] no-underline outline-none transition-colors rounded-sm focus-visible:ring-2 focus-visible:ring-fbui-ring-default [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        dark: 'text-fbui-gray-800 hover:text-fbui-gray-500',
        primary: 'text-fbui-basil-600 hover:text-fbui-basil-700',
      },
      size: {
        xs: 'gap-1.5 text-xs [&_svg]:size-3.5',
        sm: 'gap-1.5 text-sm [&_svg]:size-4',
        md: 'gap-2 text-base [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'dark', size: 'sm' },
  }
)

export interface LinkProps
  extends
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    VariantProps<typeof linkVariants> {
  /** Render the child element as the link (Radix Slot), e.g. a router <Link>. */
  asChild?: boolean
  /** Icon rendered before the text (auto-sized per `size`). */
  leftIcon?: React.ReactNode
  /** Icon rendered after the text (auto-sized per `size`). */
  rightIcon?: React.ReactNode
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      variant,
      size,
      leftIcon,
      rightIcon,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'a'
    return (
      <Comp
        ref={ref}
        className={cn(linkVariants({ variant, size, className }))}
        {...props}
      >
        {/* asChild expects a single child element; don't inject icon siblings. */}
        {asChild ? (
          children
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </Comp>
    )
  }
)
Link.displayName = 'Link'
